import type { Agent } from '@studio/models/agent';
import { nats } from '#~/nats/index.js';
import { NewtsConnection } from '#~/newts/connection.js';

/**
 * Fetch the status of the specified agent via NATS.
 *
 * Single-shot: this function does NOT retry internally. Callers are
 * responsible for retry cadence via React-Query (see useRoomAgentStatus).
 *
 * Per rule: stay-in-scope (ai.knowledge/global) — one retry source of truth.
 * Per rule: think-in-systems (ai.knowledge/global) — remove layers instead of
 * adding them. Previously this function ran its own retry loop AND callers
 * (useRoomAgentStatus) fired fire-and-forget retry chains on top of it,
 * stacking with React-Query's own refetch cycle. Three layers of retry
 * produced the PostHog `019cb0ab-2f62` "mutation request timed out" storm
 * (29,783 events in 12 days, 1,064 per user).
 * See ~/iris/notes/spec-bridge-ux-reliability-fixes.md for the investigation.
 *
 * @param agent - Agent to query.
 * @returns Tuple of [agent status, whether the agent reports in-room].
 */
// web boot recovery: ceiling for a single getStatus probe. Parameterized below for
// unit-testability, mirroring awaitAutoLinkSettle (routes/root/root-room-sync.ts).
const GET_STATUS_TIMEOUT_MS = 10_000;

// Accepts the enumeration shape (no `status`) — getStatus is what RESOLVES the
// status, so requiring a full `Agent` forced callers into a type lie. Every
// full-`Agent` caller still satisfies the widened param structurally.
export const getStatus = async (
  agent: Omit<Agent, 'status'>,
  { timeoutMs = GET_STATUS_TIMEOUT_MS }: { timeoutMs?: number } = {}
): Promise<[Agent['status'], boolean]> => {
  const offline: [Agent['status'], boolean] = [
    agent?.embedded ? 'offline-embedded' : 'offline',
    false
  ];

  const probe = async (): Promise<[Agent['status'], boolean]> => {
    const newts = <NewtsConnection>nats.agentConnection(agent.id);
    const inRoom = await newts.inRoom(agent.id);
    const pingable = inRoom && (await newts.pingAgent());

    if (inRoom && pingable) {
      return ['online', true];
    }

    const inRoom2 = await newts.inRoom(agent.id);

    return [agent?.embedded ? 'offline-embedded' : 'offline', inRoom2];
  };

  // web boot recovery: belt-and-suspenders timeout. Even with inRoom bounded at the
  // transport (newts/connection.ts), getStatus MUST be structurally incapable of
  // hanging — it gates useRoomAgentStatus's Promise.all, which gates
  // <AgentProvider>'s <Loader/> and the BootOverlay. A never-settling probe (any
  // future transport regression) degrades to offline instead of wedging boot.
  // Per rule: think-in-systems + reference-rules-in-comments (ai.knowledge/global).
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<[Agent['status'], boolean]>((resolve) => {
    timer = setTimeout(() => resolve(offline), timeoutMs);
  });

  try {
    return await Promise.race([probe(), timeout]);
  } catch {
    // Surface the failure as an offline result. React-Query will refetch
    // on the next legitimate trigger (focus after staleTime, invalidation,
    // user action) rather than hammering in a tight loop here.
    return offline;
  } finally {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  }
};
